import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { clearTokens } from '@/utils/auth';
import { supabase } from '@/utils/supabase';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in React Tree:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleLogout = () => {
    clearTokens();
    try {
      localStorage.removeItem('google_auth_success');
      localStorage.removeItem('google_user_email');
      localStorage.removeItem('google_user_name');
      sessionStorage.removeItem('ftth_push_banner_dismissed');
      sessionStorage.removeItem('ftth_session_id');
      sessionStorage.removeItem('portal_token');
      sessionStorage.removeItem('portal_session');
      sessionStorage.removeItem('oauth_state');
    } catch {}
    try {
      supabase.auth.signOut().catch(() => {});
    } catch {}
    window.location.hash = '#/login';
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #060f20 0%, #0a1628 40%, #0d2045 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            direction: 'rtl',
          }}
        >
          <div
            style={{
              background: 'rgba(13, 31, 60, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <AlertTriangle size={32} color="#f87171" />
            </div>

            <h1
              style={{
                color: '#fff',
                fontSize: '22px',
                fontWeight: 700,
                marginBottom: '12px',
              }}
            >
              حدث خطأ غير متوقع في النظام
            </h1>

            <p
              style={{
                color: '#94a3b8',
                fontSize: '14px',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              واجه التطبيق خطأً أثناء معالجة أو عرض البيانات. قد يكون هذا بسبب عدم استجابة الخادم الوطني أو مشكلة مؤقتة في الواجهة.
            </p>

            {this.state.error && (
              <div
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '12px',
                  color: '#f87171',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  textAlign: 'left',
                  maxHeight: '100px',
                  overflowY: 'auto',
                  direction: 'ltr',
                  marginBottom: '24px',
                }}
              >
                {this.state.error.toString()}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
              }}
            >
              <button
                onClick={this.handleReset}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
              >
                <RefreshCw size={16} />
                <span>إعادة المحاولة</span>
              </button>

              <button
                onClick={this.handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  color: '#f87171',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                <LogOut size={16} />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
